import { useState } from "react";
import type { CategoryEntitlement, MemberScopeMode } from "../../context/auth-me";
import { Dropdown } from "../../components/ui/dropdown";
import { useToast } from "../../components/ui/toast";
import type { MemberAccessWrite } from "../settings/settings-api";
import type { TeamMember } from "../settings/types";
import {
  ASSIGNABLE_ROLE_OPTIONS,
  draftToScope,
  isAssignableRole,
  ownerErrorMessage,
  scopeToDraft,
  type AssignableRole,
} from "./access-model";
import { AgentScopeEditor } from "./agent-scope-editor";
import { PermissionPicker } from "./permission-picker";

/** member_grants: role, inline grants and agent scope saved together, version-checked. */
export function MemberAccessEditor({ member, entitlements, memberScope, onSave, onStatus }: {
  member: TeamMember;
  entitlements: Record<string, CategoryEntitlement> | null | undefined;
  memberScope: MemberScopeMode;
  onSave: (id: string, access: MemberAccessWrite) => Promise<void>;
  onStatus: (id: string, status: "active" | "suspended") => Promise<void>;
}) {
  const toast = useToast();
  const [role, setRole] = useState<AssignableRole>(isAssignableRole(member.role) ? member.role : "member");
  const [permissions, setPermissions] = useState<string[]>(member.permissions || []);
  const [scope, setScope] = useState(() => scopeToDraft(member.resourceScope));
  const [scopeTouched, setScopeTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const suspended = member.status === "suspended";

  const save = async () => {
    setBusy(true);
    try {
      await onSave(member.id, {
        role_id: role,
        permissions,
        // Untouched stays null so a save never rewrites a scope the owner did not edit.
        resource_scope: scopeTouched ? draftToScope(scope) : null,
        expected_version: member.version ?? null,
      });
      setScopeTouched(false);
      toast.success(`Access saved for ${member.name}`);
    } catch (error) {
      const stale = (error as any)?.response?.status === 409;
      toast.danger(
        stale ? "Access changed elsewhere" : "Access update failed",
        stale ? "This member was updated by someone else. The latest access is loaded; review and save again." : ownerErrorMessage(error),
      );
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async () => {
    const next = suspended ? "active" : "suspended";
    if (next === "suspended" && !window.confirm(`Suspend ${member.name}? Their sessions and live audio end.`)) return;
    setBusy(true);
    try {
      await onStatus(member.id, next);
      toast.success(`${member.name} ${next === "active" ? "reactivated" : "suspended"}`);
    } catch (error) {
      toast.danger("Status update failed", ownerErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {suspended && (
        <p className="rounded-lg bg-soft p-3 text-sm text-ink-2">Suspended. Reactivate this member to edit their access.</p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-ink">Role</span>
        <Dropdown
          ariaLabel={`Access role for ${member.name}`}
          className="w-40"
          value={role}
          options={ASSIGNABLE_ROLE_OPTIONS}
          disabled={busy || suspended}
          onChange={setRole}
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-ink">Permissions</h4>
        <PermissionPicker
          role={role} value={permissions} onChange={setPermissions}
          entitlements={entitlements} disabled={busy || suspended}
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-ink">Agent access</h4>
        <AgentScopeEditor
          value={scope}
          onChange={(next) => { setScope(next); setScopeTouched(true); }}
          enforced={memberScope}
          disabled={busy || suspended}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => { void save(); }}
          disabled={busy || suspended}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save access
        </button>
        <button
          onClick={() => { void changeStatus(); }}
          disabled={busy}
          className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-50"
        >
          {suspended ? "Reactivate" : "Suspend"}
        </button>
      </div>
    </div>
  );
}
